import * as THREE from 'three';
import type { ShipSpec } from './shipSpec';
import {
  clusterNozzles, createDrivePlume, nozzleClassifier, plumeGain, STEADY_FX,
  type DrivePlume, type PlumeFamily, type PlumeFx, type PlumeLook, type PlumeSite,
} from './drivePlume';

/**
 * What the ship is burning, this tick, off the flight step's own numbers.
 *
 * Never the key state: the flight assist fires lateral thrusters with the
 * player's hands off the controls, so the signal for the lateral half is the
 * delta-v that tick actually spent and not an axis somebody held.
 */
export interface ThrusterDrive {
  /** True in cruise, where the hand-flight model steps. A fight sets its own. */
  cruise: boolean;
  /** The throttle the step applied along the nose, [-1, 1]. */
  throttle: number;
  /** Metres per second the assist charged to lateral thrusters this tick. */
  lateralDvMps: number;
  /** Real seconds the tick covered, so the spend above reads as a rate. */
  realDt: number;
  /**
   * The direction the lateral push acted along, in the ship's OWN axes, unit.
   * Zero when nothing pushed. A nozzle venting along `a` pushes along `-a`, so
   * its share of this is `max(0, -a . push)`.
   */
  pushBody: { x: number; y: number; z: number };
  /** Sprint boosters, [0, 1]. Nothing in flight sets it yet; the plume dojo does. */
  boost?: number;
}

export type ThrusterKind = 'main' | 'retro' | 'lateral' | 'boost';

export interface ThrusterNozzleRead {
  name: string;
  kind: ThrusterKind;
  /** The drive this nozzle resolved to, [0, 1]. */
  intensity: number;
  /** The drawn mesh's own flag. */
  visible: boolean;
  /** The engine the plume is drawn as; absent on the hull's own needle. */
  family?: PlumeFamily;
  /** The drawn plume's own length, radius and opacity, in ship-local metres.
   *  For a gas plume `opacity` is its overall gain and `radiusLocal` twice the
   *  halo's sigma at its far end. */
  lengthLocal: number;
  radiusLocal: number;
  opacity: number;
  /** Where the middle of the drawn plume landed, in CSS pixels. */
  px: { x: number; y: number };
  onScreen: boolean;
}

export interface ThrustersRead {
  suppressed: boolean;
  fixedPlume: boolean;
  lateralAlways: boolean;
  weakPlume: boolean;
  look: PlumeLook;
  hardPlume: boolean;
  cruise: boolean;
  throttle: number;
  /** What was handed in, and the rate it implies against the engine's own accel. */
  lateralDvMps: number;
  realDt: number;
  lateralRateMps2: number;
  lateralRateSmoothedMps2: number;
  accelMps2: number;
  pushBody: { x: number; y: number; z: number };
  /** The level the hull's own drive plumes were driven to, [0, 1]. */
  mainBurn: number;
  /** Whether the main drive draws its ignition and cut-off, and where it is in them. */
  transient: boolean;
  mainFx: PlumeFx;
  viewport: { width: number; height: number };
  nozzles: ThrusterNozzleRead[];
}

export interface ThrustersConfig {
  camera: THREE.PerspectiveCamera;
  /**
   * The hull the plumes hang off, in its own local metres. The stern plumes
   * are the hull's own and are found under it by name; the lateral and retro
   * nozzles are this module's and are mounted on it.
   */
  hull: THREE.Object3D;
  /** Drives the hull's own stern plumes, [0, 1]. */
  setMainBurn: (level: number) => void;
  /** Metres per second squared at full throttle; the lateral rate reads against it. */
  accelMps2: number;
  /** `?thrustersoff=1`: keep the handle, draw nothing. */
  suppressed?: boolean;
  /** `?plumefixed=1`: the stern plume at full size whenever it draws at all. */
  fixedPlume?: boolean;
  /** `?lateralalways=1`: every lateral nozzle at full, whatever was spent. */
  lateralAlways?: boolean;
  /** `?plumeweak=1`: the stern flare off, leaving the hull's own needle alone. */
  weakPlume?: boolean;
  /** Where this hull's lateral and retro nozzles sit. Default: the Pup's. */
  layout?: ThrusterLayout;
  /** The hull's spec: which engine each drive nozzle is (`drivePlume.ts`). */
  spec?: ShipSpec;
  /** `?plume=subtle|cinematic|vivid`. Default cinematic. */
  look?: PlumeLook;
  /** `?plumehard=1`: flat hard-edged plumes, the control `plume-check.mjs` must fail. */
  hardPlume?: boolean;
  /** `?plumetint=magenta`: every plume in the complaint screenshot's purple, the hue control. */
  magentaPlume?: boolean;
  /** The main drive's ignition flash and cut-off tail. Default on; the plume dojo's
   *  `?transient=0` is the control `tools/preview/drive-transient-check.mjs` must fail. */
  transient?: boolean;
}

/**
 * The nozzle stations on a given hull, in its own local metres (the builder's
 * units, before the ship's scale). The lateral ring sits at one station on the
 * hull's skin; the retro pair sits just aft of the bow, outboard of it, venting
 * forward.
 */
export interface ThrusterLayout {
  lateralZ: number;
  /** skin offsets at lateralZ: +/-x, +y and -y (a positive distance) */
  halfBeam: number;
  top: number;
  bottom: number;
  retroZ: number;
  retroX: number;
}

export interface Thrusters {
  group: THREE.Group;
  update: (drive: ThrusterDrive) => void;
  read: () => ThrustersRead;
  dispose: () => void;
}

/** The name `src/scene/custos.ts` gives the drive block's own plume meshes. */
const HULL_PLUME_NAME = 'drive-plume';
/**
 * Under this drive a nozzle is not drawn at all. Two percent of the engine's
 * own acceleration: above the float dust a push a few thousandths off an axis
 * leaves on the nozzles either side of it, and under anything a burn produces.
 */
const MIN_INTENSITY = 0.02;
/**
 * A thruster's drive floor once it draws at all. Not decoration: the assist's
 * own steady burn in a parked orbit runs at a seventh of the main engine's
 * acceleration, and linear from zero that is a plume nobody sees, which is the
 * burn being spent invisibly all over again. The stern plume carries no floor:
 * the player sets the throttle, so a burn at a quarter should look like one.
 */
const RCS_FLOOR = 0.3;
/**
 * A chemical attitude jet's exit radius, hull-local. Several times the
 * builders' own 0.02 cups: a real RCS puff is all but invisible in vacuum, and
 * at the chase camera's 18 m the one the assist fires has to read.
 */
const RCS_EXIT = 0.12;
/**
 * Seconds the lateral rate is smoothed over. The assist's spend arrives per
 * frame and a frame it skips would strobe the plume; a tenth of a second is
 * short enough that letting go of the stick puts the thrusters out within
 * three frames of settling.
 */
const LATERAL_TAU_S = 0.1;
/**
 * The main drive's start and end, on the render clock. A light-up after
 * IGNITE_COLD_S out is an ignition: the exit flashes and the plume runs out of
 * the bell over a few frames rather than appearing whole. At cut-off the exit
 * goes out and the last gas leaves the plume behind it over TAIL_S, its light
 * going with it: longer than thruster-check.mjs's 320 ms settle, so its stern
 * pair reads the off half after MAIN_OFF_SETTLE_MS instead.
 */
const IGNITE_COLD_S = 0.4;
const FLASH_TAU_S = 0.1;
const GROW_TAU_S = 0.08;
const TAIL_S = 0.5;
const TAIL_TAU_S = 0.18;
/** What an ignition's flash at 1 adds to the aft light. */
const FLASH_LIGHT = 1.5;
/** The aft light's reach in world metres, and its falloff. */
const LIGHT_DISTANCE = 6;
const LIGHT_DECAY = 2;
export interface BurnTransient {
  /** Where the burn is in its start or end; STEADY_FX throughout with the transient off. */
  readonly fx: Readonly<PlumeFx>;
  /** One frame of `dt` real seconds at the drawn level; returns the level to draw the plume at, 0 once it is out. */
  step: (level: number, dt: number) => number;
}

/** The main drive's ignition and cut-off clock. */
export function createBurnTransient(transient: boolean): BurnTransient {
  let wasLit = false;
  let outS = Infinity;
  let sinceLightS = Infinity;
  let sinceCutS = Infinity;
  let cutLevel = 0;
  let cutGrow = 1;
  const fx: PlumeFx = { ...STEADY_FX };

  function step(level: number, dt: number): number {
    const lit = level > MIN_INTENSITY;
    if (lit) {
      sinceLightS = outS >= IGNITE_COLD_S ? 0 : sinceLightS + dt;
      outS = 0;
      sinceCutS = Infinity;
      cutLevel = level;
    } else {
      sinceCutS = wasLit ? 0 : sinceCutS + dt;
      outS += dt;
    }
    wasLit = lit;
    if (!transient) {
      Object.assign(fx, STEADY_FX);
      return lit ? level : 0;
    }
    if (lit) {
      fx.flash = Math.exp(-sinceLightS / FLASH_TAU_S);
      fx.grow = 1 - Math.exp(-sinceLightS / GROW_TAU_S);
      fx.front = 0;
      fx.fade = 1;
      cutGrow = fx.grow;
      return level;
    }
    // A burn cut before it had grown out trails only as far as it reached.
    fx.flash = 0;
    fx.grow = cutGrow;
    fx.front = sinceCutS / TAIL_S;
    fx.fade = Math.exp(-sinceCutS / TAIL_TAU_S);
    return sinceCutS < TAIL_S ? cutLevel : 0;
  }

  return { fx, step };
}

/** Ship-local metres: the stations for a procedural hull with no manifest, and the plume dojo's fallback. */
export const PUP_THRUSTER_LAYOUT: ThrusterLayout = {
  lateralZ: 0.6, halfBeam: 1.05, top: 1.0, bottom: 0.9, retroZ: -3.2, retroX: 0.6,
};

/**
 * Lateral and retro nozzle stations for an authored hull. A manifest that marks
 * its jets (att.rcs, att.retro) fires from them; otherwise they are read off its
 * measured profile: the lateral ring on the skin of the first tankage module,
 * the retro pair a tenth of the hull aft of the bow, outboard of the skin
 * there. A procedural hull takes PUP_THRUSTER_LAYOUT.
 */
export function thrusterLayoutFor(spec: ShipSpec): ThrusterLayout {
  const m = spec.hull?.manifest;
  if (!m) return PUP_THRUSTER_LAYOUT;
  const p = m.profile;
  const bin = (z: number): number => Math.min(Math.max(Math.floor((z - p.z0) / p.pitch), 0), p.hw.length - 1);
  const lateralZ = spec.modules.find((mm) => mm.kind === 'tankage')?.z ?? 0;
  const retroZ = m.zRange[0] + 0.1 * m.spineLength;
  const skin = 0.05;
  const hh = p.hh[bin(lateralZ)] ?? 1;
  const out: ThrusterLayout = {
    lateralZ,
    halfBeam: (p.hw[bin(lateralZ)] ?? 1) + skin,
    top: hh + skin,
    bottom: hh + skin,
    retroZ,
    retroX: (p.hw[bin(retroZ)] ?? 0.6) + skin,
  };
  const mean = (v: number[]): number => v.reduce((a, b) => a + b, 0) / v.length;
  const rcs = m.attach.filter((a) => a.role === 'rcs');
  if (rcs.length) {
    out.lateralZ = mean(rcs.map((a) => a.at[2]));
    const beam = rcs.filter((a) => Math.abs(a.dir[0]) > 0.5).map((a) => Math.abs(a.at[0]));
    const up = rcs.filter((a) => a.dir[1] > 0.5).map((a) => a.at[1]);
    const down = rcs.filter((a) => a.dir[1] < -0.5).map((a) => -a.at[1]);
    if (beam.length) out.halfBeam = mean(beam);
    if (up.length) out.top = mean(up);
    if (down.length) out.bottom = mean(down);
  }
  const retro = m.attach.filter((a) => a.role === 'retro');
  if (retro.length) {
    out.retroZ = mean(retro.map((a) => a.at[2]));
    out.retroX = mean(retro.map((a) => Math.abs(a.at[0])));
  }
  return out;
}

interface NozzleSpec {
  name: string;
  kind: ThrusterKind;
  /** Where the nozzle mouth sits, in hull-local metres. */
  at: [number, number, number];
  /** The direction it vents, unit, in hull-local axes. */
  out: [number, number, number];
  family: PlumeFamily;
  exitRadius: number;
  /** The drive a nozzle draws at the moment it draws at all. */
  floor: number;
}

const rcs = (name: string, kind: ThrusterKind, at: [number, number, number],
  out: [number, number, number]): NozzleSpec => ({
  name, kind, at, out, family: 'rcs', exitRadius: RCS_EXIT, floor: RCS_FLOOR,
});

const nozzles = (l: ThrusterLayout): NozzleSpec[] => [
  rcs('lateral-starboard', 'lateral', [l.halfBeam, 0, l.lateralZ], [1, 0, 0]),
  rcs('lateral-port', 'lateral', [-l.halfBeam, 0, l.lateralZ], [-1, 0, 0]),
  rcs('lateral-dorsal', 'lateral', [0, l.top, l.lateralZ], [0, 1, 0]),
  rcs('lateral-ventral', 'lateral', [0, -l.bottom, l.lateralZ], [0, -1, 0]),
  rcs('retro-port', 'retro', [-l.retroX, 0, l.retroZ], [0, 0, -1]),
  rcs('retro-starboard', 'retro', [l.retroX, 0, l.retroZ], [0, 0, -1]),
];

interface Plume {
  spec: NozzleSpec;
  gas: DrivePlume;
  mesh: THREE.Mesh;
  out: THREE.Vector3;
  intensity: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const PLUME_AXIS = new THREE.Vector3(0, 0, 1);

/**
 * The thruster visuals for the cruise ship: a gas plume per drive nozzle (or
 * per cluster of them) driven by the throttle the flight step applied, sprint
 * boosters when the drive asks for them, and lateral and retro jets this
 * module owns. The hull's own needle plumes are hidden while this is mounted.
 *
 * Every plume is built at construction and only its uniforms change after.
 * The one light is the aft glow on the hull, created here once and never
 * removed, so the scene's point-light count is fixed from the first frame
 * (`tools/preview/descent-dojo.mjs --lights`); off, it sits at intensity 0.
 */
export function createThrusters(config: ThrustersConfig): Thrusters {
  const { camera, hull } = config;
  const suppressed = config.suppressed === true;
  const fixedPlume = config.fixedPlume === true;
  const lateralAlways = config.lateralAlways === true;
  const weakPlume = config.weakPlume === true;
  const look: PlumeLook = config.look ?? 'cinematic';
  const hardPlume = config.hardPlume === true;
  const magenta = config.magentaPlume === true;
  const transient = config.transient !== false;
  const accelMps2 = config.accelMps2 > 0 ? config.accelMps2 : 1;

  const group = new THREE.Group();
  group.name = 'thrusters';
  hull.add(group);
  let seed = 0;

  function mount(spec: NozzleSpec): Plume {
    const gas = createDrivePlume({
      family: spec.family, exitRadius: spec.exitRadius, look, seed: seed++, hard: hardPlume, magenta,
    });
    const mesh = gas.mesh;
    mesh.name = spec.name;
    mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
    const out = new THREE.Vector3(spec.out[0], spec.out[1], spec.out[2]).normalize();
    mesh.quaternion.setFromUnitVectors(PLUME_AXIS, out);
    // The program links on the frames the hull first draws rather than on the
    // frame a player first burns; the first update clears this.
    mesh.visible = true;
    group.add(mesh);
    return { spec, gas, mesh, out, intensity: 0 };
  }

  const plumes: Plume[] = nozzles(config.layout ?? PUP_THRUSTER_LAYOUT).map(mount);

  // The hull's own drive nozzles, found by the name custos.ts gives their
  // needles: where each sits and which way it points, in hull-local metres.
  hull.updateWorldMatrix(true, true);
  const hullPlumes: THREE.Mesh[] = [];
  hull.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && o.name === HULL_PLUME_NAME) hullPlumes.push(m);
  });
  const classify = nozzleClassifier(config.spec);
  const hullQuat = hull.getWorldQuaternion(new THREE.Quaternion()).invert();
  const sites: PlumeSite[] = hullPlumes.map((needle) => {
    const at = hull.worldToLocal(needle.getWorldPosition(new THREE.Vector3()));
    // A needle's cone points its apex up +Y and trails its body down -Y, out
    // of the nozzle (custos.ts), on both the procedural and the authored path.
    const q = hullQuat.clone().multiply(needle.getWorldQuaternion(new THREE.Quaternion()));
    const dir = new THREE.Vector3(0, -1, 0).applyQuaternion(q).normalize();
    const cone = (needle.geometry as THREE.ConeGeometry).parameters?.radius ?? 0.16;
    return { at, dir, cls: classify(at, cone * Math.abs(needle.scale.x)) };
  });
  const clusters = clusterNozzles(sites);
  const siteSpec = (s: PlumeSite, name: string, kind: ThrusterKind): NozzleSpec => ({
    name, kind, at: [s.at.x, s.at.y, s.at.z], out: [s.dir.x, s.dir.y, s.dir.z],
    family: s.cls.family, exitRadius: s.cls.exitRadius, floor: 0,
  });
  const mainPlumes: Plume[] = clusters.filter((s) => !s.cls.booster)
    .map((s, i) => mount(siteSpec(s, `main-${i}`, 'main')));
  const boostPlumes: Plume[] = clusters.filter((s) => s.cls.booster)
    .map((s, i) => mount(siteSpec(s, `boost-${i}`, 'boost')));
  // `?plumeweak=1` is the old look's own failure: the needle carrying the burn.
  for (const needle of hullPlumes) needle.visible = weakPlume;

  const allPlumes: Plume[] = [...plumes, ...mainPlumes, ...boostPlumes];

  // The aft glow: one light at the drive's centroid, just clear of the exits.
  const light = new THREE.PointLight(0xffffff, 0, LIGHT_DISTANCE, LIGHT_DECAY);
  light.name = 'thrust-light';
  let lightAtFull = 0;
  if (mainPlumes.length) {
    const c = new THREE.Vector3();
    const d = new THREE.Vector3();
    const col = new THREE.Color(0, 0, 0);
    for (const p of mainPlumes) {
      c.add(p.mesh.position);
      d.add(p.out);
      col.r += p.gas.lightColor.r; col.g += p.gas.lightColor.g; col.b += p.gas.lightColor.b;
      lightAtFull += p.gas.lightAtFull;
    }
    c.multiplyScalar(1 / mainPlumes.length);
    const r = mainPlumes.reduce((a, p) => Math.max(a, p.gas.exitRadius), 0);
    light.position.copy(c).addScaledVector(d.normalize(), 1.5 * r);
    light.color.setRGB(col.r / mainPlumes.length, col.g / mainPlumes.length, col.b / mainPlumes.length);
  }
  group.add(light);

  let warmFrames = 3;
  let renderTime = 0;
  let cruise = false;
  let throttle = 0;
  let lateralDvMps = 0;
  let realDt = 0;
  let lateralRate = 0;
  let lateralSmoothed = 0;
  let mainBurn = 0;
  const burn = createBurnTransient(transient);
  const push = new THREE.Vector3();
  const _world = new THREE.Vector3();
  const _ndc = new THREE.Vector3();
  const _vp = new THREE.Matrix4();

  function setPlume(p: Plume, intensity: number): void {
    const i = clamp01(intensity);
    p.intensity = i;
    const on = i > MIN_INTENSITY;
    p.gas.update(on ? p.spec.floor + (1 - p.spec.floor) * i : 0, renderTime);
    p.mesh.visible = on;
  }

  function update(drive: ThrusterDrive): void {
    cruise = drive.cruise;
    throttle = drive.throttle;
    lateralDvMps = drive.lateralDvMps;
    realDt = drive.realDt;
    if (realDt > 0) renderTime += realDt;
    push.set(drive.pushBody.x, drive.pushBody.y, drive.pushBody.z);
    lateralRate = realDt > 0 ? lateralDvMps / realDt : 0;
    const blend = realDt > 0 ? 1 - Math.exp(-realDt / LATERAL_TAU_S) : 1;
    lateralSmoothed += (lateralRate - lateralSmoothed) * blend;

    if (warmFrames > 0) {
      warmFrames -= 1;
      for (const p of allPlumes) {
        p.gas.update(0, renderTime);
        p.mesh.visible = true;
        p.intensity = 0;
      }
      light.intensity = 0;
      mainBurn = 0;
      config.setMainBurn(0);
      return;
    }

    if (suppressed) {
      for (const p of allPlumes) setPlume(p, 0);
      light.intensity = 0;
      mainBurn = 0;
      config.setMainBurn(0);
      return;
    }

    const forward = clamp01(throttle);
    mainBurn = fixedPlume ? (forward > MIN_INTENSITY ? 1 : 0) : forward;
    config.setMainBurn(mainBurn);
    const drawnMain = weakPlume ? 0 : mainBurn;
    const level = burn.step(drawnMain, realDt);
    const lit = drawnMain > MIN_INTENSITY;
    for (const p of mainPlumes) {
      p.intensity = lit ? drawnMain : 0;
      p.gas.update(level, renderTime, burn.fx);
      p.mesh.visible = level > 0;
    }
    const boost = weakPlume ? 0 : clamp01(drive.boost ?? 0);
    for (const p of boostPlumes) setPlume(p, boost);
    light.intensity = level > 0
      ? lightAtFull * plumeGain(level) * burn.fx.fade * (1 + FLASH_LIGHT * burn.fx.flash) : 0;

    const retro = clamp01(-throttle);
    const lateral = lateralAlways ? 1 : clamp01(lateralSmoothed / accelMps2);
    const pushed = push.lengthSq() > 0;
    for (const p of plumes) {
      if (p.spec.kind === 'retro') {
        setPlume(p, retro);
        continue;
      }
      if (lateralAlways) {
        setPlume(p, 1);
        continue;
      }
      const share = pushed ? Math.max(0, -p.out.dot(push)) : 0;
      setPlume(p, lateral * share);
    }
  }

  function project(width: number, height: number): { px: { x: number; y: number }; onScreen: boolean } {
    _ndc.copy(_world).applyMatrix4(_vp);
    const front = _ndc.z > -1 && _ndc.z < 1;
    return {
      px: { x: ((_ndc.x + 1) / 2) * width, y: ((1 - _ndc.y) / 2) * height },
      onScreen: front && Math.abs(_ndc.x) <= 1 && Math.abs(_ndc.y) <= 1,
    };
  }

  function readOne(p: Plume, width: number, height: number): ThrusterNozzleRead {
    const lengthLocal = p.gas.lengthLocal();
    // The middle of the drawn plume, not the nozzle mouth: a box centred here
    // lands on the flame rather than on the hull it comes out of.
    _world.copy(p.out).multiplyScalar(lengthLocal * 0.5).add(p.mesh.position);
    group.localToWorld(_world);
    return {
      name: p.spec.name,
      kind: p.spec.kind,
      intensity: p.intensity,
      visible: p.mesh.visible,
      family: p.gas.family,
      lengthLocal,
      radiusLocal: p.gas.radiusLocal(),
      opacity: p.mesh.visible ? p.gas.brightness() : 0,
      ...project(width, height),
    };
  }

  function readHull(mesh: THREE.Mesh, index: number, width: number, height: number): ThrusterNozzleRead {
    const material = mesh.material as THREE.MeshBasicMaterial;
    const cone = mesh.geometry as THREE.ConeGeometry;
    mesh.getWorldPosition(_world);
    const drawn = mesh.visible && material.opacity > 0;
    return {
      name: `main-core-${index}`,
      kind: 'main',
      intensity: mainBurn,
      visible: drawn,
      lengthLocal: mesh.scale.y * (cone.parameters?.height ?? 1),
      radiusLocal: mesh.scale.x * (cone.parameters?.radius ?? 0),
      opacity: drawn ? material.opacity : 0,
      ...project(width, height),
    };
  }

  function read(): ThrustersRead {
    const width = window.innerWidth;
    const height = window.innerHeight;
    camera.updateWorldMatrix(true, false);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    _vp.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    hull.updateWorldMatrix(true, true);
    return {
      suppressed,
      fixedPlume,
      lateralAlways,
      weakPlume,
      look,
      hardPlume,
      cruise,
      throttle,
      lateralDvMps,
      realDt,
      lateralRateMps2: lateralRate,
      lateralRateSmoothedMps2: lateralSmoothed,
      accelMps2,
      pushBody: { x: push.x, y: push.y, z: push.z },
      mainBurn,
      transient,
      mainFx: { ...burn.fx },
      viewport: { width, height },
      nozzles: [
        ...mainPlumes.map((p) => readOne(p, width, height)),
        ...hullPlumes.map((m, i) => readHull(m, i, width, height)),
        ...boostPlumes.map((p) => readOne(p, width, height)),
        ...plumes.map((p) => readOne(p, width, height)),
      ],
    };
  }

  function dispose(): void {
    for (const p of allPlumes) p.gas.dispose();
    for (const needle of hullPlumes) needle.visible = true;
    light.removeFromParent();
    light.dispose();
    group.removeFromParent();
  }

  return { group, update, read, dispose };
}
